// Envio do protocolo e do retorno ao cliente (#5830, projeto 11).
//
// Dois canais: e-mail (Resend, pela edge function) e WhatsApp (n8n → UaisyChat,
// no molde do fluxo "Aviso de adesão ao boleto único"). Aqui fica a parte pura:
// quais canais dá para usar, o que vai no payload e como ler a resposta.
//
// Regras que já custaram caro no projeto e valem aqui:
//   • o n8n responde 200 com corpo VAZIO quando o fluxo falha antes do respond —
//     só `ok: true` confirma (ver src/lib/liberarAssinaturasTermo.ts);
//   • o corpo pode vir com BOM (﻿) antes do JSON;
//   • UaisyChat devolve `success: true` para ENFILEIRADO, não para entregue — a
//     tela nunca pode dizer "entregue ao cliente".
//
// Sem I/O: o acesso ao banco e à edge function fica no fim do arquivo.

export type CanalEnvio = 'email' | 'whatsapp';
export type TipoEnvio = 'protocolo' | 'retorno';
export type ResultadoEnvio = 'enfileirado' | 'enviado' | 'falha' | 'sem_canal';

/** Modo de operação, no padrão do disparo do boleto único. */
export type ModoEnvio = 'desligado' | 'piloto' | 'ligado';

export interface ContatoCliente {
  email?: string | null;
  whatsapp?: string | null;
  /** Contato do solicitante (demanda 04), quando quem pediu não é o titular. */
  emailSolicitante?: string | null;
  telefoneSolicitante?: string | null;
}

/** Telefone brasileiro em formato de envio: só dígitos, com 55 na frente. */
export function normalizarTelefone(valor: string | null | undefined): string | null {
  const digitos = String(valor ?? '').replace(/\D/g, '');
  if (!digitos) return null;
  const com55 = digitos.startsWith('55') ? digitos : `55${digitos}`;
  // 55 + DDD + 8 ou 9 dígitos. Fora disso é cadastro sujo, não telefone.
  if (com55.length < 12 || com55.length > 13) return null;
  if (/^(\d)\1+$/.test(com55.slice(4))) return null;
  return com55;
}

const emailValido = (valor: string | null | undefined): string | null => {
  const e = String(valor ?? '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e) ? e : null;
};

export interface CanaisDisponiveis {
  email: string | null;
  whatsapp: string | null;
  /** Nenhum canal: o atendente precisa saber que ninguém vai receber. */
  nenhum: boolean;
}

/**
 * Para onde dá para mandar. O contato do solicitante (quem pediu) tem
 * precedência sobre o do cadastro da UC: é quem está acompanhando o pedido.
 */
export function canaisDisponiveis(contato: ContatoCliente): CanaisDisponiveis {
  const email = emailValido(contato.emailSolicitante) ?? emailValido(contato.email);
  const whatsapp = normalizarTelefone(contato.telefoneSolicitante) ?? normalizarTelefone(contato.whatsapp);
  return { email, whatsapp, nenhum: !email && !whatsapp };
}

export interface DadosMensagem {
  protocolo: string | null;
  clienteNome: string | null;
  ucNumero: string | null;
  dataSolicitacao: string | null;
  dataRetornoPrevista: string | null;
  /** Texto pronto (demanda 07). No envio do protocolo, é o aviso de recebimento. */
  texto: string;
  /** Uma linha para o {{4}} do template. Vazio cai no texto neutro. */
  resumo?: string | null;
}

export interface PayloadWhatsapp {
  telefone: string;
  nome: string;
  protocolo: string;
  uc: string;
  data_pedido: string;
  data_retorno: string;
  mensagem: string;
  /** {{4}} do template `vigor_cancelamento_retorno`: uma linha, sem quebras. */
  resumo: string;
}

/**
 * Parâmetro de template do WhatsApp Oficial não aceita quebra de linha, tab nem
 * 4 espaços seguidos — a Meta recusa a mensagem inteira (erro de formato). Todo
 * campo que vira {{n}} passa por aqui.
 */
export function umaLinha(valor: string | null | undefined): string {
  return String(valor ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * {{4}} do template: o que o cliente precisa saber em uma linha. O detalhamento
 * (demonstrativo da multa, saldo, faturas) vai no e-mail, que não tem limite de
 * formato — aqui só entra o que muda a decisão dele.
 *
 * Sem análise (validação anterior à demanda 05) a frase não inventa número.
 */
export function resumoParaTemplate(analise: AnaliseParaResumo | null | undefined): string {
  const a = analise ?? null;
  if (!a) return 'concluida. Os detalhes estao no e-mail que enviamos';

  const partes: string[] = [];
  if (a.multa?.valor && a.multa.valor.valor > 0) {
    partes.push(`multa de ${brlSimples(a.multa.valor.valor)}`);
  } else if (a.multa && !a.multa.valor) {
    partes.push('multa a confirmar');
  }
  if (a.faturasEmAberto && a.faturasEmAberto.qtd > 0 && a.faturasEmAberto.valor > 0) {
    partes.push(`${a.faturasEmAberto.qtd} ${a.faturasEmAberto.qtd === 1 ? 'fatura' : 'faturas'} em aberto somando ${brlSimples(a.faturasEmAberto.valor)}`);
  }
  const dias = Number(a.avisoPrevioDias ?? 0);
  if (dias > 0) partes.push(`${dias} dias de aviso previo`);

  if (partes.length === 0) return 'concluida, sem pendencias financeiras';
  if (partes.length === 1) return `concluida, com ${partes[0]}`;
  return `concluida, com ${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
}

/** Só o que o resumo usa — evita amarrar esta lib ao formato inteiro da análise. */
export interface AnaliseParaResumo {
  faturasEmAberto?: { qtd: number; valor: number } | null;
  multa?: { valor: { valor: number } | null } | null;
  avisoPrevioDias?: number | null;
}

const brlSimples = (v: number) =>
  `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const soData = (v: string | null | undefined): string => {
  const t = String(v ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
  return t.split('-').reverse().join('/');
};

/**
 * Corpo que vai para o n8n. Os nomes aqui são os mesmos que precisam existir em
 * "Variáveis Customizadas do Payload" do gatilho na UaisyChat — mudar um nome
 * aqui sem mudar lá faz a mensagem chegar com o campo vazio.
 */
export function payloadWhatsapp(telefone: string, dados: DadosMensagem): PayloadWhatsapp {
  return {
    telefone,
    nome: umaLinha(dados.clienteNome) || 'cliente',
    protocolo: umaLinha(dados.protocolo),
    uc: umaLinha(dados.ucNumero),
    data_pedido: soData(dados.dataSolicitacao),
    data_retorno: soData(dados.dataRetornoPrevista),
    // `mensagem` continua indo inteira: serve ao histórico do fluxo e a um
    // eventual envio por conversa aberta. O template usa `resumo`.
    mensagem: String(dados.texto ?? '').trim(),
    resumo: umaLinha(dados.resumo) || resumoParaTemplate(null),
  };
}

export function assuntoEmail(tipo: TipoEnvio, protocolo: string | null): string {
  const p = String(protocolo ?? '').trim();
  const base = tipo === 'protocolo'
    ? 'Recebemos seu pedido de cancelamento'
    : 'Retorno da análise do seu pedido de cancelamento';
  return p ? `${base} — ${p}` : base;
}

/** Corpo do e-mail: o mesmo texto da mensagem, em parágrafos. */
export function corpoEmailHtml(dados: DadosMensagem): string {
  const escapar = (s: string) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const paragrafos = String(dados.texto ?? '')
    .split(/\n{2,}/)
    .map(p => escapar(p.trim()).replace(/\n/g, '<br>'))
    .filter(Boolean)
    .map(p => `<p style="margin:0 0 12px">${p}</p>`)
    .join('');
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333">${paragrafos}</div>`;
}

export interface RespostaCanal {
  canal: CanalEnvio;
  resultado: ResultadoEnvio;
  detalhe: string | null;
}

/**
 * Lê a resposta do n8n. 200 com corpo vazio NÃO é sucesso: o fluxo pode ter
 * falhado antes do nó de resposta. Só `ok: true` confirma, e mesmo assim o
 * resultado é "enfileirado" — a UaisyChat aceitou, o cliente ainda não recebeu.
 */
export function interpretarRespostaN8n(status: number, corpo: string | null | undefined): RespostaCanal {
  const bruto = String(corpo ?? '').replace(/^﻿/, '').trim();
  if (status < 200 || status >= 300) {
    return { canal: 'whatsapp', resultado: 'falha', detalhe: `n8n respondeu ${status}${bruto ? `: ${bruto.slice(0, 200)}` : ''}` };
  }
  if (!bruto) {
    return { canal: 'whatsapp', resultado: 'falha', detalhe: 'n8n respondeu 200 com corpo vazio — o fluxo falhou antes de responder' };
  }
  let json: any;
  try {
    json = JSON.parse(bruto);
  } catch {
    return { canal: 'whatsapp', resultado: 'falha', detalhe: `resposta do n8n não é JSON: ${bruto.slice(0, 200)}` };
  }
  const dado = Array.isArray(json) ? json[0] : json;
  if (dado?.ok === true || dado?.valido === true || dado?.success === true) {
    return { canal: 'whatsapp', resultado: 'enfileirado', detalhe: null };
  }
  const motivo = dado?.erro || dado?.error || dado?.message;
  return { canal: 'whatsapp', resultado: 'falha', detalhe: motivo ? String(motivo).slice(0, 200) : 'n8n não confirmou o envio (sem ok: true)' };
}

/** Texto curto do resultado, para a tela. Nunca diz "entregue". */
export function rotuloResultado(canal: CanalEnvio, resultado: ResultadoEnvio): string {
  if (resultado === 'sem_canal') return canal === 'email' ? 'sem e-mail cadastrado' : 'sem WhatsApp cadastrado';
  if (resultado === 'falha') return canal === 'email' ? 'e-mail falhou' : 'WhatsApp falhou';
  if (canal === 'email') return 'e-mail enviado';
  return 'WhatsApp enfileirado';
}

/** O disparo automático só acontece no modo ligado; piloto registra sem enviar. */
export function deveDisparar(modo: ModoEnvio | string | null | undefined): boolean {
  return String(modo ?? '').trim() === 'ligado';
}

// ===========================================================================
// I/O
// ===========================================================================

export interface EnvioRegistrado {
  canal: CanalEnvio;
  resultado: ResultadoEnvio;
  detalhe: string | null;
}

/**
 * Manda pela edge function, que é quem fala com o Resend e com o n8n (as chaves
 * não podem ficar no navegador). Erro de `functions.invoke` esconde a causa: o
 * motivo real vem em `error.context` — mesmo padrão de src/lib/gatewayResolver.ts.
 */
export async function enviarMensagemCancelamento(
  solicitacaoId: number,
  tipo: TipoEnvio,
  texto: string,
): Promise<EnvioRegistrado[]> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.functions.invoke('enviar-cancelamento-mensagem', {
    body: { solicitacao_id: solicitacaoId, tipo, texto },
  });
  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || detalhe?.message || error.message);
  }
  return (data?.envios || []) as EnvioRegistrado[];
}

export async function listarEnviosDaSolicitacao(solicitacaoId: number) {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('cancelamento_envios')
    .select('id, tipo, canal, resultado, detalhe, destino, created_at')
    .eq('solicitacao_id', solicitacaoId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ===========================================================================
// Linha do tempo (critério de aceite da #5830)
// ===========================================================================
//
// O histórico da solicitação é gravado por trigger no Postgres, e o trigger só
// dispara quando `status` muda. Envio de mensagem não muda status, então nunca
// apareceria lá. Em vez de mexer num trigger SECURITY DEFINER que já roda em
// produção, a tela junta as duas fontes aqui — o atendente vê uma linha do
// tempo só, na ordem em que as coisas aconteceram.

export interface EventoLinhaDoTempo {
  /** Único dentro da lista; `h-` para histórico, `e-` para envio. */
  chave: string;
  quando: string;
  titulo: string;
  detalhe: string | null;
  autor: string | null;
  tipo: 'status' | 'envio';
  /** Envio que falhou — a tela destaca. */
  falhou: boolean;
}

export interface ItemHistoricoStatus {
  id: number | string;
  status_novo: string;
  observacao?: string | null;
  alterado_em: string;
  alterado_por_nome?: string | null;
}

export interface ItemEnvio {
  id: number | string;
  tipo: string;
  canal: string;
  resultado: string;
  destino?: string | null;
  detalhe?: string | null;
  created_at: string;
}

const TITULO_TIPO: Record<string, string> = {
  protocolo: 'Aviso de protocolo',
  retorno: 'Retorno da análise',
};

/**
 * Junta status e envios numa lista só, do mais antigo para o mais novo. Data
 * inválida ou ausente não derruba a lista: o item entra no fim, porque sumir
 * com um envio que falhou é pior do que mostrá-lo fora de ordem.
 */
export function linhaDoTempo(
  historico: ItemHistoricoStatus[] | null | undefined,
  envios: ItemEnvio[] | null | undefined,
  rotuloStatus: (status: string) => string,
): EventoLinhaDoTempo[] {
  const eventos: EventoLinhaDoTempo[] = [];

  for (const h of historico || []) {
    eventos.push({
      chave: `h-${h.id}`,
      quando: h.alterado_em,
      titulo: rotuloStatus(h.status_novo),
      detalhe: h.observacao?.trim() || null,
      autor: h.alterado_por_nome || null,
      tipo: 'status',
      falhou: false,
    });
  }

  for (const e of envios || []) {
    const titulo = TITULO_TIPO[e.tipo] || 'Mensagem ao cliente';
    const canal = e.canal === 'email' ? 'e-mail' : 'WhatsApp';
    const partes = [
      `${canal}: ${rotuloResultado(e.canal as CanalEnvio, e.resultado as ResultadoEnvio)}`,
      e.destino || null,
      e.detalhe || null,
    ].filter(Boolean);
    eventos.push({
      chave: `e-${e.id}`,
      quando: e.created_at,
      titulo,
      detalhe: partes.join(' · '),
      autor: null,
      tipo: 'envio',
      falhou: e.resultado === 'falha',
    });
  }

  const instante = (v: string) => {
    const t = Date.parse(String(v ?? ''));
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
  };
  return eventos.sort((a, b) => instante(a.quando) - instante(b.quando));
}

// ===========================================================================
// Situação do envio na listagem (critério de aceite da #5830)
// ===========================================================================

export interface SituacaoEnvio {
  /** Último envio de algum canal falhou e não houve sucesso depois. */
  precisaReenviar: boolean;
  canaisComFalha: CanalEnvio[];
  /** Houve ao menos um envio que a operadora aceitou. */
  algumAceito: boolean;
  /** Texto para o botão da listagem. */
  rotulo: string;
  /** Explicação para o `title`, sempre citando o canal. */
  explicacao: string;
}

const NOME_CANAL: Record<string, string> = { email: 'e-mail', whatsapp: 'WhatsApp' };

/**
 * O que a listagem deve dizer sobre o retorno já enviado.
 *
 * Mostrar "Resposta enviada" com um certinho verde quando o WhatsApp falhou faz
 * o atendente acreditar que o cliente foi avisado. Aqui vale o ÚLTIMO envio de
 * cada canal: uma falha antiga seguida de sucesso não pede reenvio.
 *
 * `sem_canal` (cliente sem e-mail ou sem WhatsApp cadastrado) não é falha — é
 * o caso normal de 2 em cada 3 clientes.
 */
export function situacaoEnvio(envios: ItemEnvio[] | null | undefined, tipo: TipoEnvio = 'retorno'): SituacaoEnvio {
  const doTipo = (envios || []).filter(e => e.tipo === tipo);
  const instante = (v: string) => {
    const t = Date.parse(String(v ?? ''));
    return Number.isFinite(t) ? t : 0;
  };

  const ultimoPorCanal = new Map<string, ItemEnvio>();
  for (const e of doTipo) {
    const atual = ultimoPorCanal.get(e.canal);
    if (!atual || instante(e.created_at) >= instante(atual.created_at)) ultimoPorCanal.set(e.canal, e);
  }

  const canaisComFalha = Array.from(ultimoPorCanal.values())
    .filter(e => e.resultado === 'falha')
    .map(e => e.canal as CanalEnvio);
  const algumAceito = Array.from(ultimoPorCanal.values())
    .some(e => e.resultado === 'enviado' || e.resultado === 'enfileirado');

  if (canaisComFalha.length === 0) {
    return {
      precisaReenviar: false, canaisComFalha: [], algumAceito,
      rotulo: doTipo.length > 0 ? 'Resposta enviada' : 'Responder cliente',
      explicacao: '',
    };
  }

  const nomes = canaisComFalha.map(c => NOME_CANAL[c] || c).join(' e ');
  return {
    precisaReenviar: true,
    canaisComFalha,
    algumAceito,
    rotulo: 'Reenviar',
    explicacao: algumAceito
      ? `O envio por ${nomes} falhou. O outro canal saiu — reveja a mensagem e reenvie.`
      : `O envio por ${nomes} falhou e o cliente não foi avisado por nenhum canal.`,
  };
}

/** Envios de várias solicitações de uma vez, para a listagem não fazer N buscas. */
export async function resumirEnviosPorSolicitacao(ids: number[]): Promise<Map<number, ItemEnvio[]>> {
  const mapa = new Map<number, ItemEnvio[]>();
  const unicos = Array.from(new Set(ids.filter(n => Number.isFinite(n))));
  if (unicos.length === 0) return mapa;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('cancelamento_envios')
    .select('id, solicitacao_id, tipo, canal, resultado, destino, detalhe, created_at')
    .in('solicitacao_id', unicos);
  if (error) throw error;
  for (const row of (data || []) as any[]) {
    const lista = mapa.get(row.solicitacao_id) || [];
    lista.push(row);
    mapa.set(row.solicitacao_id, lista);
  }
  return mapa;
}

// ===========================================================================
// Disparo automático do aviso de protocolo (#5830)
// ===========================================================================
//
// ⚠️ Nasce DESLIGADO: `system_settings.cancelamento_envio_modo` = 'desligado'.
// Enquanto ninguém ligar, esta função não envia nada — e é assim de propósito.
// Quem decide ligar é a operação (Vinicius Alves), na tarefa de pendências.
//
// Só o AVISO DE PROTOCOLO é automático. O retorno da análise continua saindo
// pela mão de um atendente, que lê a mensagem antes: ela cita multa, saldo e
// faturas, e é o documento que o cliente guarda para cobrar a Vigor depois.

/** Lê o modo de envio. Erro de leitura vira 'desligado' — no escuro, não dispara. */
export async function lerModoEnvio(): Promise<ModoEnvio> {
  try {
    const { supabase } = await import('./supabase');
    const { data } = await supabase.from('system_settings')
      .select('value').eq('key', 'cancelamento_envio_modo').maybeSingle();
    const v = String((data as any)?.value ?? '').trim();
    return (v === 'ligado' || v === 'piloto') ? v : 'desligado';
  } catch {
    return 'desligado';
  }
}

export interface DadosAvisoProtocolo {
  solicitacaoId: number;
  clienteNome: string | null;
  protocolo: string | null;
  ucNumero: string | null;
  dataSolicitacao: string | null;
  dataRetornoPrevista: string | null;
}

/**
 * Chamada logo depois de a solicitação nascer. NUNCA lança: o pedido do cliente
 * já está gravado, e uma falha de mensagem não pode fazer a tela dizer que o
 * pedido não foi registrado. O que der errado fica em `cancelamento_envios`.
 *
 * Devolve `false` quando não tentou (modo desligado), o que a tela não precisa
 * mostrar — é o estado normal de hoje.
 */
export async function dispararAvisoProtocolo(dados: DadosAvisoProtocolo): Promise<boolean> {
  try {
    if (!deveDisparar(await lerModoEnvio())) return false;
    const { textoAvisoProtocolo } = await import('./mensagemRetornoCancelamento');
    const texto = textoAvisoProtocolo({
      clienteNome: dados.clienteNome,
      protocolo: dados.protocolo,
      ucNumero: dados.ucNumero,
      dataSolicitacao: dados.dataSolicitacao,
      dataRetornoPrevista: dados.dataRetornoPrevista,
    });
    const { supabase } = await import('./supabase');
    const { error } = await supabase.functions.invoke('enviar-cancelamento-mensagem', {
      body: { solicitacao_id: dados.solicitacaoId, tipo: 'protocolo', texto, automatico: true },
    });
    if (error) throw error;
    return true;
  } catch (e) {
    console.error('Aviso de protocolo não enviado (o pedido foi registrado normalmente):', e);
    return false;
  }
}
